import { roundToNearestHundred } from '../../core/Core';
import type { ExternalPortCostEstimates } from '../../hooks/usePortCostEstimates';
import {
  PORT_COST_MISSING_PORT_TYPE_KEY,
  PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
} from '../../hooks/usePortCostEstimates';
import { getCurrencyRate, type GlobalStore } from '../../../state/zustand';
import type { CostParam } from './costs';
import { PortType } from '@seaber/common/graphql/types';

export type HistoricalPortCostEstimates = {
  min: number | undefined;
  avg: number | undefined;
  median: number | undefined;
  max: number | undefined;
  count: number;
  currency: string;
  cost: number;
  match: 'type' | 'name';
  typeAttributeId?: number;
  portType?: PortType | null;
};

function findEffectivePortCostHistory(
  map: ExternalPortCostEstimates,
  key1: string, // port name
  key2: string, // vessel name or primary attribute id
  key3: string | null, // port type
): ReturnType<typeof map.get> | undefined {
  const portFunctionKey = key3 ?? PORT_COST_MISSING_PORT_TYPE_KEY;

  // 1: Find match by specific port type
  const portCostEstimate = map.get(key1, key2, portFunctionKey);
  if (portCostEstimate) {
    return portCostEstimate;
  }

  // 2: If type 'M' not found, fallback to 'L' or 'D'
  if (key3 === PortType.M) {
    // try 'L'
    const portCostLoad = map.get(key1, key2, PortType.L);
    if (portCostLoad) {
      return portCostLoad;
    }
    // try 'D'
    const portCostDischarge = map.get(key1, key2, PortType.D);
    if (portCostDischarge) {
      return portCostDischarge;
    }
  }

  return map.get(key1, key2, PORT_COST_MISSING_PORT_TYPE_KEY);
}

export function getHistoricalPortCostEstimates({
  locationName,
  vesselName,
  primaryVesselAttributeId,
  byName,
  byPrimaryAttributeId,
  portType,
}: {
  locationName: string;
  vesselName: string;
  primaryVesselAttributeId: number | null | undefined;
  byName: ExternalPortCostEstimates;
  byPrimaryAttributeId: ExternalPortCostEstimates;
  portType?: PortType;
}): HistoricalPortCostEstimates | undefined {
  locationName = locationName.toUpperCase();
  let costByType = primaryVesselAttributeId
    ? findEffectivePortCostHistory(
        byPrimaryAttributeId,
        locationName,
        primaryVesselAttributeId.toString(),
        portType ?? null,
      )
    : undefined;

  if (!costByType) {
    costByType = findEffectivePortCostHistory(
      byPrimaryAttributeId,
      locationName,
      PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
      portType ?? null,
    );
  }

  if (costByType?.median) {
    return {
      ...costByType,
      cost: roundToNearestHundred(costByType.median),
      match: 'type',
      typeAttributeId: primaryVesselAttributeId ?? undefined,
    };
  }
  const costByName = findEffectivePortCostHistory(
    byName,
    locationName,
    vesselName,
    portType ?? null,
  );
  if (costByName?.median) {
    return {
      ...costByName,
      cost: roundToNearestHundred(costByName.median),
      match: 'name',
      portType,
    };
  }
  return undefined;
}

export function mapExternalPortCostEstimatesToParams(
  pce: HistoricalPortCostEstimates | undefined,
  currencyRates: GlobalStore['currencyRates'],
) {
  const { min, max, count, match, currency, portType } = { ...pce };
  const params: CostParam[] = [];

  if (min) {
    params.push({
      testId: 'port-cost-min',
      name: 'Min',
      value: min,
      source: 'SEABER',
    });
  }

  if (max) {
    params.push({
      testId: 'port-cost-max',
      name: 'Max',
      value: max,
      source: 'SEABER',
    });
  }

  if (count) {
    params.push({
      testId: 'port-cost-similar-calls',
      name: 'Similar calls',
      value: count,
      source: 'SEABER',
    });
  }

  if (match) {
    params.push({
      testId: 'port-cost-match-by-vessel',
      name: 'Match by vessel',
      value: match,
      source: 'SEABER',
    });
  }

  if (portType) {
    params.push({
      testId: 'port-cost-port-type',
      name: 'Port type',
      value: portType,
      source: 'SEABER',
    });
  }

  if (currency && currencyRates && currency !== currencyRates?.baseCurrency) {
    params.push({
      testId: 'port-cost-original-currency',
      name: 'Original currency',
      value: currency,
      source: 'SEABER',
    });
    params.push({
      testId: 'port-cost-exchange-rate',
      name: 'Exchange rate',
      value: `1 ${currencyRates?.baseCurrency} = ${getCurrencyRate(
        currency,
      )} ${currency}`,
      source: 'SEABER',
    });
  }

  return params;
}
